import React, { PureComponent } from "react"
import classNames from "classnames"
import { Query, Mutation } from "react-apollo"

import Head from "../../components/head-data/head-data"
import RequestSignIn from "../../components/request-sign-in/request-sign-in"
import FounderHero from "../../components/founder-hero/founder-hero"
import FounderProgressRibbon from "../../components/founder-progress-ribbon/founder-progress-ribbon"
import FounderReading from "../../components/founder-reading/founder-reading"
import FounderMotivationalCallout from "../../components/founder-motivational-callout/founder-motivational-callout"
import FounderPlanDocument from "../../components/founder-plan-document/founder-plan-document"
import FounderQuestionnaire from "../../components/founder-questionnaire/founder-questionnaire"
import FounderActionsList from "../../components/founder-actions-list/founder-actions-list"
import FounderRecommendations from "../../components/founder-recommendations/founder-recommendations"
import FounderCheckinHistory from "../../components/founder-checkin-history/founder-checkin-history"

import {
  MY_FOUNDER_ASSIST_QUERY,
  UPDATE_FOUNDER_ASSIST_MODE,
} from "../../lib/founder-assist-queries"
import { PollWhile } from "../../lib/founder-assist-jobs"

import "./founder-assist.scss"

class FounderAssistPage extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      profileExpanded: false,
      historyOpen: false,
      historyAutoExpandFirst: false,
    }
  }

  pickInitialMode = (mode, updateMode) => {
    updateMode({ variables: { mode } })
  }

  toggleProfile = () => {
    this.setState((p) => ({ profileExpanded: !p.profileExpanded }))
  }

  handleHistoryToggle = (open) => {
    this.setState({
      historyOpen: open,
      historyAutoExpandFirst: false,
    })
  }

  handleShowHistoryFromCallout = () => {
    this.setState({
      historyOpen: true,
      historyAutoExpandFirst: true,
    })
    if (typeof window !== "undefined") {
      setTimeout(() => {
        const el = document.getElementById("history")
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "start" })
        }
      }, 50)
    }
  }

  render() {
    const { me } = this.props
    if (!me || !me.id) {
      return (
        <>
          <Head
            title="FounderAssist — plan your next 6 months"
            description="Use FounderAssist to draft a summary, six-month plan, and weekly action list tailored to your startup."
          />
          <RequestSignIn />
        </>
      )
    }

    return (
      <>
        <Head
          title="FounderAssist — plan your next 6 months"
          description="Use FounderAssist to draft a summary, six-month plan, and weekly action list tailored to your startup."
        />
        <Query
          query={MY_FOUNDER_ASSIST_QUERY}
          fetchPolicy="cache-and-network"
          ssr={false}
        >
          {({ data, loading, startPolling, stopPolling }) => (
            <>
              {/* Plan generation runs in the background — poll until done. */}
              <PollWhile
                active={Boolean(
                  data &&
                    data.myFounderAssist &&
                    data.myFounderAssist.generationInProgress
                )}
                startPolling={startPolling}
                stopPolling={stopPolling}
              />
              {this.renderBody(me, data, loading)}
            </>
          )}
        </Query>
      </>
    )
  }

  renderModePicker(fa) {
    return (
      <Mutation
        mutation={UPDATE_FOUNDER_ASSIST_MODE}
        refetchQueries={[{ query: MY_FOUNDER_ASSIST_QUERY }]}
      >
        {(updateMode) => (
          <section className="founder-assist-page__mode-section">
            <div className="founder-assist-page__eyebrow">
              Where would you like to start?
            </div>
            <div className="founder-assist-page__mode-cards">
              <button
                type="button"
                className={classNames("founder-assist-page__mode-card", {
                  "founder-assist-page__mode-card--active":
                    !fa || fa.mode === "company",
                })}
                onClick={() => this.pickInitialMode("company", updateMode)}
              >
                <span className="founder-assist-page__mode-icon" aria-hidden>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="7" width="18" height="13" rx="2" />
                    <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </span>
                <strong>Plan for my company</strong>
                <span>
                  Plans grounded in your company's goals,
                  resources, and constraints.
                </span>
              </button>
              <button
                type="button"
                className={classNames("founder-assist-page__mode-card", {
                  "founder-assist-page__mode-card--active":
                    fa && fa.mode === "individual",
                })}
                onClick={() => this.pickInitialMode("individual", updateMode)}
              >
                <span className="founder-assist-page__mode-icon" aria-hidden>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 21v-1a8 8 0 0 1 16 0v1" />
                  </svg>
                </span>
                <strong>Plan for me as a founder</strong>
                <span>
                  Plans grounded in your skills, interests, and
                  personal goals.
                </span>
              </button>
            </div>
          </section>
        )}
      </Mutation>
    )
  }

  renderBody(me, data, loading) {
    const fa = data && data.myFounderAssist
    const hasQuestionnaire = Boolean(fa && fa.questionnaireSavedAt)
    const hasPlan = Boolean(
      fa && (fa.summaryParagraph || fa.sixMonthPlanParagraph)
    )

    if (loading && !fa) {
      return (
        <div className="founder-assist-page">
          <FounderHero me={me} fa={null} />
          <div className="founder-assist-page__shell">
            <div className="founder-assist-page__loading">
              Loading FounderAssist…
            </div>
          </div>
        </div>
      )
    }

    // STATE A — empty: mode picker + questionnaire (no plan doc)
    // STATE B — questionnaire saved, no plan yet: just the questionnaire.
    // A and B share one tree so the questionnaire stays mounted when the
    // first save flips A → B. A remount would re-read `me` before its
    // refetch lands and blank the field the user just filled in.
    if (!hasQuestionnaire || !hasPlan) {
      return (
        <div className="founder-assist-page">
          <FounderHero me={me} fa={fa} />
          <div className="founder-assist-page__shell">
            {!hasQuestionnaire && this.renderModePicker(fa)}
            <section className="founder-assist-page__section">
              <FounderQuestionnaire
                fa={fa}
                me={me}
                showGenerateCta={true}
              />
            </section>
          </div>
        </div>
      )
    }

    // STATE C — the dashboard (also `/` for members): progress, this week's
    // plan, who to talk to, what to read, then the longer outlook.
    const isPremium = Boolean(me.hasValidCoFounderSubscription)
    return (
      <div className="founder-assist-page">
        <FounderHero me={me} fa={fa} />
        <div className="founder-assist-page__shell">
          <section className="founder-assist-page__section founder-assist-page__section--progress">
            <FounderProgressRibbon fa={fa} isPremium={isPremium} />
          </section>

          {fa.lastMotivationalStatement && (
            <section className="founder-assist-page__section founder-assist-page__section--callout">
              <FounderMotivationalCallout
                fa={fa}
                onShowHistory={this.handleShowHistoryFromCallout}
              />
            </section>
          )}

          <section
            id="actions"
            className="founder-assist-page__section founder-assist-page__section--actions"
          >
            <FounderActionsList actions={fa.actions} />
          </section>

          <section
            id="recommendations"
            className="founder-assist-page__section founder-assist-page__section--recommendations"
          >
            <FounderRecommendations isPremium={isPremium} />
          </section>

          <section className="founder-assist-page__section founder-assist-page__section--reading">
            <FounderReading me={me} />
          </section>

          <section className="founder-assist-page__section founder-assist-page__section--plan">
            <FounderPlanDocument fa={fa} />
          </section>

          {fa.lastCheckinAt && (
            <section
              id="history"
              className="founder-assist-page__section founder-assist-page__section--history"
            >
              <FounderCheckinHistory
                open={this.state.historyOpen}
                onToggle={this.handleHistoryToggle}
                autoExpandFirst={this.state.historyAutoExpandFirst}
              />
            </section>
          )}

          <section
            id="refine"
            className={classNames(
              "founder-assist-page__profile",
              {
                "founder-assist-page__profile--open":
                  this.state.profileExpanded,
              }
            )}
          >
            <button
              type="button"
              className="founder-assist-page__profile-toggle"
              onClick={this.toggleProfile}
              aria-expanded={this.state.profileExpanded}
            >
              <span className="founder-assist-page__profile-chevron" aria-hidden />
              <span className="founder-assist-page__profile-title">
                Refine the answers your plan is built on
              </span>
              <span className="founder-assist-page__profile-meta">
                {fa.mode === "individual" ? "Individual" : "Company"}{" "}
                profile
              </span>
            </button>
            {this.state.profileExpanded && (
              <div className="founder-assist-page__profile-body">
                <FounderQuestionnaire fa={fa} me={me} />
              </div>
            )}
          </section>
        </div>
      </div>
    )
  }
}

export default FounderAssistPage
